import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import '@/features/common/pagination.css';
import { Icon } from '@/features/common/Icon';
import { paginationWindow } from '@/features/common/paginationWindow';
import { usePaginationNeighbours } from '@/features/common/usePaginationNeighbours';
import type { PaginationProps } from '@/features/common/types/PaginationProps';

/**
 * The pager, in one place.
 *
 * <p>Lifted out of NewsPage when the library became its second user, and out of publicsite when
 * the contact admin list became its second feature. Labels arrive as props rather than being read
 * from a dictionary here: the pages own different namespaces, and a shared component that reached
 * into one of them would make the others' translations depend on a page they have nothing to do
 * with.
 *
 * <p>Two ways to move a page, and they are not interchangeable. A page of a public listing is a
 * URL — that is what makes a filtered result shareable, lets the back button work, and lets a
 * crawler reach page four — so those callers pass `hrefFor` and get links. An admin list that
 * keeps its filters in component state passes `onPage` and gets buttons, since paging such a
 * screen through the URL would be the only half of its state that survived a reload. (Some
 * screens under /app do drive their filters from the URL — the audit journal and the news table
 * among them — and those pass `onPage` while writing the page to the address themselves.) The
 * props type is a discriminated union, so the compiler refuses both at once and refuses neither.
 */
export function Pagination(props: PaginationProps) {
  const { page, totalPages, labels } = props;
  // Before the early return: a hook cannot be called conditionally, and a pager with one page
  // still mounts and unmounts as its list is filtered.
  const neighbours = usePaginationNeighbours();
  if (totalPages <= 1) return null;

  // Same class, same content, same box — see pagination.css, which sets the padding, height,
  // border and background a bare <button> would otherwise take from the button base rule.
  const control = (to: number, children: ReactNode, ariaLabel?: string) =>
    props.hrefFor ? (
      <Link className="pagination__link" to={props.hrefFor(to)} aria-label={ariaLabel}>
        {children}
      </Link>
    ) : (
      <button
        type="button"
        className="pagination__link"
        onClick={() => props.onPage(to)}
        aria-label={ariaLabel}
      >
        {children}
      </button>
    );

  return (
    <nav className="pagination" aria-label={labels.region}>
      <ul className="pagination__list">
        <li className="pagination__item">
          {/* At a boundary this stays in the DOM and stops being a control, matching the
              server-rendered markup: a disabled <a> without href is skipped by tab order,
              where hiding it would make the row jump as the visitor pages through. */}
          {page === 0 ? (
            <span className="pagination__link pagination__link--disabled" aria-disabled="true">
              <Icon name="chevron-left" className="pagination__icon" />
              <span className="pagination__link--text">{labels.previous}</span>
            </span>
          ) : (
            control(
              page - 1,
              <>
                <Icon name="chevron-left" className="pagination__icon" />
                <span className="pagination__link--text">{labels.previous}</span>
              </>,
              // Named explicitly because pagination.css hides the label under 768px, as MUD
              // does: the accessible name cannot be the only thing a `display: none` removes.
              labels.previous,
            )
          )}
        </li>

        {paginationWindow(page, totalPages, neighbours).map((p, i) =>
          p === null ? (
            // Index is a stable key here: the window is derived from (page, totalPages,
            // neighbours), so a gap only changes position when the whole list is rebuilt anyway.
            <li className="pagination__item" key={`gap-${i}`}>
              <span className="pagination__ellipsis" aria-hidden="true">
                …
              </span>
            </li>
          ) : (
            <li className="pagination__item" key={p}>
              {p === page ? (
                <span
                  className="pagination__link pagination__link--active"
                  aria-current="page"
                  aria-label={labels.current(p + 1)}
                >
                  {p + 1}
                </span>
              ) : (
                control(p, p + 1, labels.page(p + 1))
              )}
            </li>
          ),
        )}

        <li className="pagination__item">
          {page >= totalPages - 1 ? (
            <span className="pagination__link pagination__link--disabled" aria-disabled="true">
              <span className="pagination__link--text">{labels.next}</span>
              <Icon name="chevron-right" className="pagination__icon" />
            </span>
          ) : (
            control(
              page + 1,
              <>
                <span className="pagination__link--text">{labels.next}</span>
                <Icon name="chevron-right" className="pagination__icon" />
              </>,
              labels.next,
            )
          )}
        </li>
      </ul>
    </nav>
  );
}
